import {
  cancel,
  fork,
  take,
  put,
  call,
  takeLatest,
  select,
  takeEvery,
} from 'redux-saga/effects'
import moment from 'moment'
import { capitalize, isEmpty, debounce } from 'lodash-es'
import { reset as resetForm, getFormValues } from 'redux-form/dist/redux-form'

import { showNotice } from '../../../Layout/Notices/Notices'

import {
  fetchComments,
  fetchComment,
  addComment,
  deleteComment,
  updateComment,
  postCommentSetOrder,
} from './api'

const debouncedSetQuestionsOrder = debounce(postCommentSetOrder, 1000)

export default ({ actionNames, props, getFeatureRoot }) => {
  const { commentType, order, chatRoom } = props
  const selectorType = commentType === 'question' ? 'questions' : 'chat'

  const getComments = (state) => getFeatureRoot(state)[selectorType].comments
  const getWebinarsCurrentPage = (state) =>
    getFeatureRoot(state)[selectorType].currentPage
  const getWatchingRealtimeStatus = (state) =>
    getFeatureRoot(state)[selectorType].isWatchingRealtime
  const getPresenterToken = (state) =>
    getFeatureRoot(state)[selectorType].presenterToken
  const getWebinarTitle = (state) => {
    if (state.webinar && state.webinar.liveData) {
      return state.webinar.liveData.name
    }

    if (state.webinarAudienceRoom && state.webinarAudienceRoom.liveData) {
      return state.webinarAudienceRoom.liveData.name
    }

    return 'Webinars'
  }

  const getActions = (isInfiniteLoad) => {
    if (isInfiniteLoad) {
      if (commentType === 'question') {
        return {
          success: actionNames.INFINITE_LOAD_QUESTIONS_SUCCESS,
          failure: actionNames.INFINITE_LOAD_QUESTIONS_FAILURE,
        }
      }

      return {
        success: actionNames.INFINITE_LOAD_CHAT_SUCCESS,
        failure: actionNames.INFINITE_LOAD_CHAT_FAILURE,
      }
    }

    if (commentType === 'question') {
      return {
        success: actionNames.FETCH_QUESTIONS_SUCCESS,
        failure: actionNames.FETCH_QUESTIONS_FAILURE,
      }
    }

    return {
      success: actionNames.FETCH_CHAT_SUCCESS,
      failure: actionNames.FETCH_CHAT_FAILURE,
    }
  }

  const getInitLeaveActions = () => {
    if (commentType === 'question') {
      return {
        initAction: actionNames.INIT_QUESTIONS,
        leaveAction: actionNames.LEAVE_QUESTIONS,
      }
    }

    return {
      initAction: actionNames.INIT_CHAT,
      leaveAction: actionNames.LEAVE_CHAT,
    }
  }

  function* fetchCommentsWorker({ liveId, page = 1, isInfiniteLoad = false }) {
    const { isGroupedByStatus } = props
    const actions = getActions(isInfiniteLoad)
    const presenterToken = yield select(getPresenterToken)
    let size = 40

    if (commentType === 'question') {
      // if the questions are grouped by status there is no pagination, setting a
      // high size to not limit the number of questions.
      if (isGroupedByStatus) {
        size = 5000
      }
    }

    const webinarTitle = yield select(getWebinarTitle)

    try {
      const { comments, total_count: totalCount } = yield call(
        fetchComments,
        liveId,
        chatRoom ?? commentType,
        { presenterToken, order, size },
        page
      )
      yield put({
        type: actions.success,
        comments,
        totalCount,
        page,
        webinarTitle,
      })
    } catch (err) {
      showNotice({
        message: `${capitalize(commentType)} list could not be loaded`,
        expirationMillis: 5e3,
        isClosable: true,
      })
      yield put({ type: actions.failure })
    }
  }

  function* fetchSingleCommentWorker({ commentId, liveId }) {
    const action =
      commentType === 'question'
        ? 'FETCH_SINGLE_QUESTION_SUCCESS'
        : 'FETCH_SINGLE_CHAT_MESSAGE_SUCCESS'
    const presenterToken = yield select(getPresenterToken)

    try {
      const { comment } = yield call(
        fetchComment,
        commentId,
        liveId,
        presenterToken
      )

      // questions can be created anonymously and
      // then are not allowed to be accessed anonymously,
      // so we will only list them
      if (commentType === 'question' && isEmpty(comment)) {
        return
      }

      yield put({ type: actionNames[action], comment })

      if (commentType !== 'question') {
        yield put({
          type: actionNames.SET_SHOULD_SCROLL,
          shouldScroll: true,
        })
      }
    } catch (err) {
      showNotice({
        message: `${capitalize(commentType)} message could not be loaded`,
        expirationMillis: 5000,
        isClosable: true,
      })
    }
  }

  function* optimisticallyAddCommentWorker(content, commentId, name) {
    const fullName = name || props.localParticipantName

    if (isEmpty(fullName)) {
      yield
      return
    }

    const xHtmlTags = /<\/?\w+[^>]*\/?>/gi

    const comment = {
      content_text: content.replace(xHtmlTags, ''),
      comment_id: commentId,
      entry_date_ansi: moment().format('YYYY-MM-DD HH:mm:ss'),
      name: fullName,
    }

    const action =
      commentType === 'question'
        ? 'OPTIMISTICALLY_ADD_QUESTION'
        : 'OPTIMISTICALLY_ADD_CHAT_MESSAGE'

    yield put({ type: actionNames[action], comment })
  }

  function* addCommentWorker({ values, formSubmitPromise }) {
    const { resolve, reject } = formSubmitPromise
    const { name, content } = values
    let resetAction
    let failureAction

    if (commentType === 'question') {
      resetAction = actionNames.RESET_ADD_QUESTION_BUTTON_STATE
      failureAction = actionNames.ADD_QUESTION_FAILURE
    } else {
      resetAction = actionNames.RESET_ADD_CHAT_MESSAGE_BUTTON_STATE
      failureAction = actionNames.ADD_CHAT_MESSAGE_FAILURE
    }

    try {
      const { liveId, token, uuid } = props

      const userInfo = { name, uuid }
      yield put(resetForm(`${commentType}Form`))
      yield put({ type: resetAction })
      resolve()

      const { comment } = yield call(
        addComment,
        liveId,
        token,
        content,
        chatRoom ?? commentType,
        userInfo
      )

      yield call(
        optimisticallyAddCommentWorker,
        content,
        comment.comment_id,
        name
      )
      yield call(fetchSingleCommentWorker, {
        commentId: comment.comment_id,
        liveId,
      })
    } catch (err) {
      showNotice({
        message: `The ${commentType} list could not be loaded`,
        expirationMillis: 5000,
        isClosable: true,
      })
      yield put({ type: failureAction })
      reject()
    }
  }

  function* watchAdd() {
    const action =
      commentType === 'question' ? 'ADD_QUESTION' : 'ADD_CHAT_MESSAGE'

    yield takeEvery(actionNames[action], addCommentWorker)
  }

  function* addChatReactionWorker({ reaction }) {
    const formValues = yield select(getFormValues('chatForm'))

    try {
      const { liveId, token, uuid, localParticipantName } = props
      const { colon } = reaction
      const name = formValues?.name || localParticipantName
      const userInfo = { name, uuid }

      const { comment } = yield call(
        addComment,
        liveId,
        token,
        colon,
        chatRoom ?? commentType,
        userInfo
      )

      yield call(
        optimisticallyAddCommentWorker,
        colon,
        comment.comment_id,
        name
      )
      yield call(fetchSingleCommentWorker, {
        commentId: comment.comment_id,
        liveId,
      })
    } catch (err) {
      showNotice({
        message: `The ${commentType} list could not be loaded`,
        expirationMillis: 5000,
        isClosable: true,
      })
    }
  }

  function* watchAddReaction() {
    yield takeEvery(actionNames.ADD_CHAT_REACTION, addChatReactionWorker)
  }

  function* deleteCommentWorker({ commentId }) {
    try {
      yield call(deleteComment, props.liveId, commentId)
    } catch (err) {
      showNotice({
        message: 'The item could not be deleted',
        expirationMillis: 5000,
        isClosable: true,
      })
    }
  }

  function* watchDelete() {
    const action =
      commentType === 'question' ? 'DELETE_QUESTION' : 'DELETE_CHAT_MESSAGE'

    yield takeLatest(actionNames[action], deleteCommentWorker)
  }

  function* moveCommentWorker({ fromIndex, toIndex }) {
    try {
      const comments = yield select(getComments)
      const commentsList = comments.toList()

      let newFromIndex = fromIndex
      let newToIndex = toIndex
      commentsList.forEach((comment, index) => {
        if (comment.comment_status !== '') {
          if (index <= newFromIndex) {
            newFromIndex += 1
          }

          if (index <= newToIndex) {
            newToIndex += 1
          }
        }
        return index < newFromIndex || index < newToIndex
      })

      const comment = commentsList.get(newFromIndex)
      const newComments = commentsList
        .delete(newFromIndex)
        .insert(newToIndex, comment)
        .toArray()

      const action =
        commentType === 'question'
          ? 'OPTIMISTICALLY_UPDATE_QUESTION_COMMENTS'
          : 'OPTIMISTICALLY_UPDATE_CHAT_MESSAGE_COMMENTS'

      yield put({ type: actionNames[action], comments: newComments })
      yield call(debouncedSetQuestionsOrder, props.liveId, newComments)
    } catch (err) {
      showNotice({
        message: 'Could not set the order of comments',
        expirationMillis: 5000,
        isClosable: true,
      })
    }
  }

  function* watchMoveComment() {
    const action =
      commentType === 'question'
        ? 'MOVE_QUESTION_COMMENT'
        : 'MOVE_CHAT_MESSAGE_COMMENT'

    yield takeEvery(actionNames[action], moveCommentWorker)
  }

  function* watchInfiniteScroll() {
    const blockingAction =
      commentType === 'question'
        ? 'FETCH_QUESTIONS_SUCCESS'
        : 'FETCH_CHAT_SUCCESS'
    const infiniteAction =
      commentType === 'question'
        ? 'INFINITE_LOAD_QUESTIONS'
        : 'INFINITE_LOAD_CHAT'

    yield take(actionNames[blockingAction])
    const { liveId } = props

    yield takeLatest(actionNames[infiniteAction], function* onInfiniteLoad() {
      const currentPage = yield select(getWebinarsCurrentPage)
      const page = currentPage + 1
      yield call(fetchCommentsWorker, { liveId, page, isInfiniteLoad: true })
    })
  }

  function* realtimeOrderCommentsWorker(realtimeData) {
    const newOrder = realtimeData?.payload?.order || []
    const comments = yield select(getComments)
    const orderedComments = newOrder.map((commentId) => comments.get(commentId))

    const action =
      commentType === 'question'
        ? 'OPTIMISTICALLY_UPDATE_QUESTION_COMMENTS'
        : 'OPTIMISTICALLY_UPDATE_CHAT_MESSAGE_COMMENTS'

    yield put({ type: actionNames[action], comments: orderedComments })
  }

  function* realtimeUpdateCommentWorker(realtimeData) {
    const { comment_id: commentId, comment_status: status } =
      realtimeData?.payload ?? {}
    const comments = yield select(getComments)
    const comment = comments.get(commentId)
    if (comment) {
      comment.comment_status = status
      const newComments = comments.set(commentId, comment)
      const action =
        commentType === 'question'
          ? 'OPTIMISTICALLY_UPDATE_QUESTION_COMMENTS'
          : 'OPTIMISTICALLY_UPDATE_CHAT_MESSAGE_COMMENTS'

      yield put({
        type: actionNames[action],
        comments: newComments.toArray(),
      })
    }
  }

  function* watchRealTime() {
    const isWatchingRealtime = yield select(getWatchingRealtimeStatus)

    if (isWatchingRealtime) {
      return
    }

    const realtimeAction =
      commentType === 'question'
        ? actionNames.WATCH_REALTIME_QUESTIONS
        : actionNames.WATCH_REALTIME_CHAT
    yield put({ type: realtimeAction })

    yield takeEvery(
      actionNames.REALTIME_DATA_UPDATE,
      function* onDataUpdate(action) {
        const { realtimeData } = action

        if (!realtimeData) {
          yield
          return
        }

        if (realtimeData.event === 'add') {
          if (realtimeData.comment_type !== commentType) {
            if (realtimeData.comment_type === chatRoom) {
              realtimeData.comment_type = 'chat'
            } else {
              yield
              return
            }
          }
          yield fork(fetchSingleCommentWorker, {
            commentId: realtimeData.comment_id,
            liveId: realtimeData.object_id,
          })
        } else if (realtimeData.event === 'delete') {
          yield put({
            type: actionNames.REALTIME_REMOVE_COMMENT,
            commentId: realtimeData.comment_id,
          })
        } else if (realtimeData.event === 'comments_order') {
          const realtimeDataCommentType = realtimeData?.payload?.comment_type
          if (
            realtimeDataCommentType &&
            realtimeDataCommentType === commentType
          ) {
            yield fork(realtimeOrderCommentsWorker, realtimeData)
          }
        } else if (realtimeData.event === 'comment_update') {
          if (commentType === 'question') {
            yield fork(realtimeUpdateCommentWorker, realtimeData)
          }
        }
      }
    )
  }

  function* watchLeave(action, tasks) {
    yield take(action)
    yield tasks.map((task) => cancel(task))

    const resetAction =
      commentType === 'question' ? 'RESET_QUESTIONS' : 'RESET_CHAT'
    yield put({ type: actionNames[resetAction] })
  }

  function* updateCommentWorker({ commentId, status }) {
    const comments = yield select(getComments)
    const comment = comments.get(commentId)
    comment.comment_status = status
    const newComments = comments.set(commentId, comment)
    const action =
      commentType === 'question'
        ? 'OPTIMISTICALLY_UPDATE_QUESTION_COMMENTS'
        : 'OPTIMISTICALLY_UPDATE_CHAT_MESSAGE_COMMENTS'

    yield put({ type: actionNames[action], comments: newComments.toArray() })
    yield call(updateComment, props.liveId, commentId, status)
  }

  function* watchUpdateComment() {
    const action =
      commentType === 'question'
        ? 'UPDATE_QUESTION_COMMENT'
        : 'UPDATE_CHAT_MESSAGE_COMMENT'

    yield takeEvery(actionNames[action], updateCommentWorker)
  }

  function* init() {
    const tasks = []
    const { initAction, leaveAction } = getInitLeaveActions()

    while (true) {
      const action = yield take(initAction)
      if (!props.doNotFetchQuestions) {
        tasks.push(yield fork(fetchCommentsWorker, action))
      }
      tasks.push(yield fork(watchAdd))
      tasks.push(yield fork(watchDelete))
      tasks.push(yield fork(watchRealTime))
      tasks.push(yield fork(watchInfiniteScroll))
      tasks.push(yield fork(watchAddReaction))
      tasks.push(yield fork(watchMoveComment))
      tasks.push(yield fork(watchUpdateComment))
      yield call(watchLeave, leaveAction, tasks)
    }
  }

  return {
    default: [init()],
    watchAdd,
    watchDelete,
    watchMoveComment,
    watchInfiniteScroll,
    init,
    watchLeave,
    fetchCommentsWorker,
    addCommentWorker,
    deleteCommentWorker,
    watchRealTime,
    addChatReactionWorker,
    watchAddReaction,
    watchUpdateComment,
  }
}
